/**
 * Analytics helper — Google Analytics 4 + Meta Pixel with EU consent management.
 *
 * Implementation notes:
 *   • Google Consent Mode v2: GA4 always loads but starts with `denied` defaults
 *     (set in index.html). On consent granted we call gtag('consent', 'update', …)
 *     so GA4 starts dropping real cookies. On denied, GA4 continues to send
 *     anonymous pings (cookieless ping mode).
 *   • Meta Pixel: more conservative — we DO NOT fire fbq at all until the user
 *     has explicitly granted consent. fbq calls before consent are silently
 *     no-op'd.
 *   • Single source of truth for consent is window.localStorage.cookieConsent
 *     ('granted' | 'denied' | missing).
 */
const CONSENT_KEY = "cookieConsent";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const GA4_ID = process.env.REACT_APP_GA4_ID;

export const getConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY); // 'granted' | 'denied' | null
  } catch (_) {
    return null;
  }
};

export const hasConsented = () => getConsent() === "granted";

/** Inject the Meta Pixel base script the first time we have consent. */
const ensureMetaPixelLoaded = () => {
  if (!META_PIXEL_ID || typeof window === "undefined") return;
  if (window.__metaPixelLoaded) return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
  window.__metaPixelLoaded = true;
};

/** Called when the user clicks "Accepter" in the cookie banner. */
export const grantConsent = () => {
  try { localStorage.setItem(CONSENT_KEY, "granted"); } catch (_) {}
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_user_data: "granted",
      ad_personalization: "granted",
      ad_storage: "granted",
      analytics_storage: "granted",
    });
  }
  ensureMetaPixelLoaded();
};

/** Called when the user clicks "Refuser". */
export const denyConsent = () => {
  try { localStorage.setItem(CONSENT_KEY, "denied"); } catch (_) {}
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_user_data: "denied",
      ad_personalization: "denied",
      ad_storage: "denied",
      analytics_storage: "denied",
    });
  }
};

/** Called once at app boot — if consent was previously granted, replay it. */
export const initAnalyticsFromStoredConsent = () => {
  if (getConsent() === "granted") grantConsent();
};

/**
 * Track a custom event in both GA4 (always — Consent Mode handles anonymisation)
 * and Meta Pixel (only when consent granted).
 *
 * @param {string} name     - GA4 event name (snake_case)
 * @param {object} params   - GA4 event params
 * @param {string} fbEvent  - Meta standard event name (e.g. 'Lead', 'Contact')
 * @param {object} fbParams - Meta event params
 */
export const track = (name, params = {}, fbEvent = null, fbParams = {}) => {
  if (window.gtag && GA4_ID) {
    window.gtag("event", name, params);
  }
  if (fbEvent && window.fbq && hasConsented()) {
    window.fbq("track", fbEvent, fbParams);
  }
};
